import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authService } from "../services/api";

const Login = () => {
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loginMethod, setLoginMethod] = useState("oauth"); // 'oauth' or 'org-token'
  const [apiToken, setApiToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Check if we're on mobile by default
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      return true;
    }
    return false;
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if there's an error in URL params
    const queryParams = new URLSearchParams(location.search);
    const errorMsg = queryParams.get("error");
    if (errorMsg) {
      setError(errorMsg);
    }

    // Check if already authenticated
    const checkAuth = async () => {
      try {
        const { isAuthenticated } = await authService.checkAuthStatus();
        if (isAuthenticated) {
          navigate("/dashboard");
        }
      } catch (error) {
        console.error("Auth check failed:", error);
      }
    };

    checkAuth();
  }, [location, navigate]);

  useEffect(() => {
    // Handle window resize to auto-collapse on mobile
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogin = () => {
    authService.login();
  };

  const handleOrgTokenLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await authService.orgTokenLogin(apiToken);
      navigate("/dashboard");
    } catch (error) {
      console.error("Org token login failed:", error);
      setError(
        error.response?.data?.message ||
          "Authentication failed. Please check your credentials.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-cf-gray-50">
      {/* Left Side - Branding & Info */}
      <div
        className={`bg-white border-r border-cf-gray-200 flex flex-col transition-all duration-300 ${
          isCollapsed ? "w-0 md:w-16" : "w-80"
        } ${isCollapsed ? "overflow-hidden" : ""}`}
      >
        {/* Features Section */}
        {!isCollapsed && (
          <div className="flex-1 p-6">
            <h3 className="text-xs font-semibold text-cf-gray-500 uppercase tracking-wider mb-4">
              Features
            </h3>
            <div className="space-y-4">
              <div className="bg-cf-gray-50 border border-cf-gray-200 p-4 rounded-cf">
                <h4 className="font-semibold text-cf-gray-900 mb-2 flex items-center text-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2 text-do-blue"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  Historical Costs
                </h4>
                <p className="text-xs text-cf-gray-600">
                  Analyze billing periods with detailed breakdowns by service
                  type and spending patterns.
                </p>
              </div>

              <div className="bg-cf-gray-50 border border-cf-gray-200 p-4 rounded-cf">
                <h4 className="font-semibold text-cf-gray-900 mb-2 flex items-center text-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2 text-do-blue"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
                    />
                  </svg>
                  Live Droplet Costs
                </h4>
                <p className="text-xs text-cf-gray-600">
                  Monitor current Droplet costs in real-time, categorized by
                  region and tags.
                </p>
              </div>

              <div className="bg-cf-gray-50 border border-cf-gray-200 p-4 rounded-cf">
                <h4 className="font-semibold text-cf-gray-900 mb-2 flex items-center text-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2 text-do-blue"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                    />
                  </svg>
                  Bandwidth Analysis
                </h4>
                <p className="text-xs text-cf-gray-600">
                  Track bandwidth usage and costs across your infrastructure.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        {!isCollapsed && (
          <div className="p-6 border-t border-cf-gray-200">
            <div className="text-xs text-cf-gray-500">
              <p className="mb-2">
                Created by{" "}
                <a
                  href="https://www.jackpearce.co.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-do-blue hover:underline"
                >
                  Jack Pearce
                </a>
              </p>
              <div>
                <a
                  href="https://github.com/digitalocean-labs/cost-explorer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-do-blue hover:underline"
                >
                  GitHub
                </a>
              </div>
              <p className="mt-3 text-cf-gray-400">
                This is <strong>not</strong> an official DigitalOcean app
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-cf-gray-200 px-4 py-3">
          <div className="flex items-center gap-4">
            {/* Logo and Title */}
            <div className="flex items-center gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="65.2 173.5 32 32"
                width="32"
                height="32"
                fill="#0080FF"
              >
                <path d="M81.202 205.5v-6.2c6.568 0 11.666-6.5 9.144-13.418a9.27 9.27 0 0 0-5.533-5.531c-6.912-2.502-13.425 2.575-13.425 9.14H65.2c0-10.463 10.124-18.622 21.1-15.195 4.8 1.505 8.618 5.313 10.105 10.1 3.43 10.99-4.717 21.107-15.203 21.107z" />
                <path d="M75.05 199.317v-6.165h6.168v6.165zm-4.753 4.75v-4.75h4.753v4.75h-4.753zm0-4.75h-3.973v-3.97h3.973v3.97z" />
              </svg>
              <div>
                <div className="font-semibold text-base text-cf-gray-900">
                  DO Cost Explorer
                </div>
                <div className="text-xs text-cf-gray-600">
                  Comprehensive cost analysis for your DigitalOcean resources
                </div>
              </div>
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-cf-gray-900 mb-2">
                Welcome
              </h1>
              <p className="text-sm text-cf-gray-600">
                Sign in to access your cost analysis dashboard
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-white border border-cf-gray-300 rounded-cf-md text-cf-gray-800">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-cf-gray-600 mr-2 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{error}</span>
                </div>
              </div>
            )}

            {/* Login Method Toggle */}
            <div className="bg-white border border-cf-gray-200 rounded-cf-md p-6 mb-6 shadow-cf">
              <h2 className="text-lg font-medium text-cf-gray-900 mb-4">
                Authentication Method
              </h2>
              <div className="flex bg-cf-gray-100 rounded-cf p-1">
                <button
                  onClick={() => setLoginMethod("oauth")}
                  className={`flex-1 py-2 px-4 rounded-cf text-sm font-medium transition-all ${
                    loginMethod === "oauth"
                      ? "bg-white text-cf-gray-900 shadow-cf"
                      : "text-cf-gray-600 hover:text-cf-gray-900"
                  }`}
                >
                  OAuth Login
                  <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-do-blue/10 text-do-blue rounded">
                    Teams only
                  </span>
                </button>
                <button
                  onClick={() => setLoginMethod("org-token")}
                  className={`flex-1 py-2 px-4 rounded-cf text-sm font-medium transition-all ${
                    loginMethod === "org-token"
                      ? "bg-white text-cf-gray-900 shadow-cf"
                      : "text-cf-gray-600 hover:text-cf-gray-900"
                  }`}
                >
                  Organization Token
                </button>
              </div>
            </div>

            {loginMethod === "oauth" ? (
              <>
                <div className="bg-white border border-cf-gray-200 rounded-cf-md p-6 mb-6 shadow-cf">
                  <button
                    onClick={handleLogin}
                    className="w-full bg-do-blue hover:bg-do-blue-600 text-white font-medium py-3 px-4 rounded-cf flex items-center justify-center transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="65.2 173.5 32 32"
                      width="32"
                      height="32"
                      fill="#ffffff"
                      className="mr-2"
                    >
                      <path d="M81.202 205.5v-6.2c6.568 0 11.666-6.5 9.144-13.418a9.27 9.27 0 0 0-5.533-5.531c-6.912-2.502-13.425 2.575-13.425 9.14H65.2c0-10.463 10.124-18.622 21.1-15.195 4.8 1.505 8.618 5.313 10.105 10.1 3.43 10.99-4.717 21.107-15.203 21.107z" />
                      <path d="M75.05 199.317v-6.165h6.168v6.165zm-4.753 4.75v-4.75h4.753v4.75h-4.753zm0-4.75h-3.973v-3.97h3.973v3.97z" />
                    </svg>
                    Login with DigitalOcean
                  </button>
                </div>

                <div className="bg-white border border-cf-gray-200 rounded-cf-md p-6 mb-6 shadow-cf">
                  <h2 className="text-lg font-medium text-cf-gray-900 mb-4">
                    About OAuth Authentication
                  </h2>
                  <p className="text-sm text-cf-gray-700 mb-4">
                    Connect your DigitalOcean account to view cost data using{" "}
                    <a
                      href="https://docs.digitalocean.com/reference/api/oauth/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-do-blue hover:underline"
                    >
                      OAuth authentication
                    </a>
                    . This secure protocol enables access to your account
                    information without requiring your password.
                  </p>

                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center text-do-blue font-medium hover:text-do-blue-700 w-full justify-between transition-colors text-sm"
                  >
                    <span>How does this tool work?</span>
                    <svg
                      className={`w-5 h-5 transition-transform ${showDetails ? "transform rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </button>

                  {showDetails && (
                    <div className="mt-4 p-4 bg-cf-gray-50 rounded-cf text-sm text-cf-gray-700 border border-cf-gray-200">
                      <h3 className="font-semibold mb-3 text-cf-gray-900">
                        Technical details:
                      </h3>
                      <ul className="list-disc pl-5 space-y-2">
                        <li>
                          <span className="font-medium">
                            OAuth Authentication:
                          </span>{" "}
                          When you click "Login with DigitalOcean", you're
                          redirected to DigitalOcean's authorization page. After
                          approval, DigitalOcean sends an authorization code to
                          our server.
                        </li>
                        <li>
                          <span className="font-medium">API Integration:</span>{" "}
                          Our server exchanges this code for a Read-Only access
                          token, which is used to securely request your account
                          data from DigitalOcean's API.
                        </li>
                        <li>
                          <span className="font-medium">
                            Session Management:
                          </span>{" "}
                          A secure HTTP-only cookie is created in your browser
                          that contains a session ID (not your DigitalOcean
                          credentials). This allows you to stay logged in.
                        </li>
                        <li>
                          <span className="font-medium">Data Privacy:</span>{" "}
                          Your billing data is processed in your browser and is
                          not stored on our servers. The session cookie is
                          automatically expired after 45 days.
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="bg-white border border-cf-gray-200 rounded-cf-md p-6 mb-6 shadow-cf">
                  <h2 className="text-lg font-medium text-cf-gray-900 mb-4">
                    Organization Token
                  </h2>

                  <form onSubmit={handleOrgTokenLogin} className="space-y-4">
                    <div>
                      <label
                        htmlFor="apiToken"
                        className="block text-sm font-medium text-cf-gray-700 mb-2"
                      >
                        Organization API Token
                      </label>
                      <input
                        id="apiToken"
                        type="password"
                        value={apiToken}
                        onChange={(e) => setApiToken(e.target.value)}
                        placeholder="dop_v1_..."
                        className="w-full px-3 py-2 border border-cf-gray-300 rounded-cf text-sm focus:outline-none focus:ring-2 focus:ring-do-blue focus:border-transparent font-mono"
                        required
                        disabled={isLoading}
                      />
                      <p className="mt-2 text-xs text-cf-gray-500">
                        Token must have{" "}
                        <code className="px-1 py-0.5 bg-cf-gray-100 rounded">
                          billing:read
                        </code>{" "}
                        scope
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-do-blue hover:bg-do-blue-600 text-white font-medium py-3 px-4 rounded-cf flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Authenticating...
                        </>
                      ) : (
                        "Login with Organization Token"
                      )}
                    </button>
                  </form>
                </div>

                <div className="bg-white border border-cf-gray-200 rounded-cf-md p-6 mb-6 shadow-cf">
                  <h2 className="text-lg font-medium text-cf-gray-900 mb-4">
                    <a
                      href="https://docs.digitalocean.com/platform/organizations/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cf-gray-900 hover:text-do-blue hover:underline"
                    >
                      About Organization Tokens
                    </a>
                  </h2>
                  <p className="text-sm text-cf-gray-700 mb-4">
                    Use an{" "}
                    <a
                      href="https://docs.digitalocean.com/reference/api/create-personal-access-token/#organization-api-tokens"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-do-blue hover:underline"
                    >
                      Organization API Token
                    </a>{" "}
                    to access billing data for your organization.
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-sm text-cf-gray-700">
                    <li>
                      Create a token with{" "}
                      <code className="px-1 py-0.5 bg-cf-gray-100 rounded text-xs">
                        billing:read
                      </code>{" "}
                      scope
                    </li>
                    <li>Only Historical Costs Dashboard will be accessible</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Login;
